import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";

const getAuthToken = () =>
  localStorage.getItem("authToken")    ||
  localStorage.getItem("accessToken")  ||
  localStorage.getItem("access_token") ||
  localStorage.getItem("token") || "";

const authHeaders = () => ({
  "Content-Type": "application/json",
  ...(getAuthToken() ? { Authorization: `Bearer ${getAuthToken()}` } : {}),
});

const fmt = (v) => `₹${Number(v || 0).toLocaleString("en-IN")}`;

// ─────────────────────────────────────────────────────────────────────────────
// PAYMENT PAGE
// Reached via: navigate("/payment", { state: { paymentOrder, orderPayload, breakdown } })
//
// Flow — paymentOrder.is_test decides which path runs (set server-side by
// views.py's RAZORPAY_MODE, not anything here):
//  Test mode:  skip the Razorpay widget, verify always succeeds (backend
//              mock) → place order → clear cart → success screen.
//  Live mode:  open the real Razorpay checkout widget → its handler gets a
//              genuinely signed payment → POST /api/payment/verify/ with
//              that real signature (backend checks it via HMAC-SHA256; a
//              missing/forged signature is rejected as "Signature
//              mismatch") → same place-order/clear-cart/success steps.
// ─────────────────────────────────────────────────────────────────────────────
const PaymentPage = () => {
  const location = useLocation();
  const navigate  = useNavigate();

  const { paymentOrder, orderPayload, breakdown } = location.state || {};

  const [paying,  setPaying]  = useState(false);
  const [error,   setError]   = useState("");
  const [success, setSuccess] = useState(false);

  if (!paymentOrder || !orderPayload) {
    return (
      <div style={S.page}>
        <div style={S.container}>
          <p style={{ color: "#b91c1c", marginBottom: 16 }}>
            Invalid payment session — no order data found.
          </p>
          <button style={S.backBtn} onClick={() => navigate("/cart-summary")}>
            ← Back to Checkout
          </button>
        </div>
      </div>
    );
  }

  const total      = breakdown?.total      ?? orderPayload?.total_amount ?? 0;
  const subtotal   = breakdown?.subtotal   ?? total;
  const discount   = breakdown?.discount   ?? 0;
  const gst        = breakdown?.gst        ?? 0;
  const platformFee = breakdown?.platformFee ?? 0;
  const tip        = breakdown?.tip        ?? Number(orderPayload?.tip_amount || 0);
  const coupon     = breakdown?.coupon     ?? null;

  const loadRazorpayScript = () => new Promise((resolve) => {
    if (window.Razorpay) { resolve(true); return; }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload  = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });

  // Step 2 + 3 — place the order with payment proof attached, clear the
  // cart, show the success screen. Shared by both the test-mode path and
  // the live Razorpay handler below, once either has a verified payment.
  const finishAfterVerify = async (verifyData) => {
    // Most flows go through the generic cart checkout endpoint; a few
    // (e.g. the Deep Cleaning wizard) submit to their own dedicated
    // booking endpoint instead — orderPayload.__submit_endpoint carries
    // that override when present.
    const { __submit_endpoint, __redirect_after_pay, ...cleanOrderPayload } = orderPayload || {};
    const submitEndpoint = __submit_endpoint || "/api/user/place-order/";
    const placeRes = await fetch(`${API_BASE_URL}${submitEndpoint}`, {
      method:      "POST",
      credentials: "include",
      headers:     authHeaders(),
      body: JSON.stringify({
        ...cleanOrderPayload,
        razorpay_payment_id:     verifyData.payment_id,
        razorpay_order_id:       paymentOrder.order_id,
        razorpay_transaction_id: verifyData.transaction_id,
        payment_status:          "paid",
      }),
    });
    const placeData = await placeRes.json();
    if (!placeRes.ok || !placeData?.success) {
      throw new Error(placeData?.message || `Order failed (HTTP ${placeRes.status})`);
    }

    // Coupon was only validated (never marked used) at the summary step —
    // redeem it now that payment is actually confirmed and the order exists.
    if (coupon?.code && placeData?.data?.order_id) {
      fetch(`${API_BASE_URL}/api/user/coupon/redeem/`, {
        method: "POST", credentials: "include", headers: authHeaders(),
        body: JSON.stringify({ mobile: orderPayload?.mobile, coupon_code: coupon.code, booking_id: placeData.data.order_id }),
      }).catch(() => {});
    }

    const mobile = orderPayload?.mobile;
    if (mobile) {
      fetch(`${API_BASE_URL}/api/user/user-cart/${encodeURIComponent(mobile)}/clear/`, {
        method: "DELETE", credentials: "include", headers: authHeaders(),
      }).catch(() => {});
    }
    localStorage.removeItem("cartItems");
    window.dispatchEvent(new Event("cart-updated"));

    setSuccess(true);
    setTimeout(() => {
      if (__redirect_after_pay) {
        // Flow-specific override (e.g. Instant tracking page) — takes
        // priority over the generic cart-checkout destinations below.
        navigate(__redirect_after_pay, { replace: true });
      } else if (tip > 0) {
        // A tip was added → skip the bookings dashboard, go straight home.
        navigate("/", { replace: true, state: { tipThanks: true } });
      } else {
        navigate("/profile-dashboard", {
          replace: true,
          state: {
            openBookings:   true,
            paymentSuccess: true,
            orderId: placeData?.data?.order_id || "",
          },
        });
      }
    }, 2200);
  };

  const verifyPayment = async (body) => {
    const verifyRes = await fetch(`${API_BASE_URL}/api/payment/verify/`, {
      method: "POST", headers: authHeaders(), body: JSON.stringify(body),
    });
    const verifyData = await verifyRes.json();
    if (!verifyRes.ok || !verifyData?.success) {
      throw new Error(verifyData?.message || "Payment verification failed.");
    }
    return verifyData;
  };

  const handlePay = async () => {
    if (paying) return;
    setPaying(true);
    setError("");
    try {
      if (paymentOrder.is_test) {
        // Test mode — no real payment gateway involved; the backend's
        // verify endpoint always succeeds for this mode, so there's
        // nothing to sign or check.
        const verifyData = await verifyPayment({
          razorpay_order_id:   paymentOrder.order_id,
          razorpay_payment_id: `pay_TEST_${Date.now()}`,
        });
        await finishAfterVerify(verifyData);
        return;
      }

      // Live mode — open Razorpay's real checkout widget and only proceed
      // once it reports back a genuinely signed payment. This page used to
      // skip the widget entirely and post an empty signature straight to
      // verify — which is exactly what surfaced as "Signature mismatch"
      // once the backend started checking it for real.
      const loaded = await loadRazorpayScript();
      if (!loaded) throw new Error("Razorpay failed to load. Please check your connection and try again.");

      await new Promise((resolve, reject) => {
        const rzp = new window.Razorpay({
          key:         paymentOrder.key_id,
          amount:      paymentOrder.amount,
          currency:    paymentOrder.currency,
          order_id:    paymentOrder.order_id,
          name:        "K2Karmika",
          description: "Service Payment",
          handler: async (response) => {
            try {
              const verifyData = await verifyPayment({
                razorpay_order_id:   response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature:  response.razorpay_signature,
              });
              await finishAfterVerify(verifyData);
              resolve();
            } catch (e) { reject(e); }
          },
          modal: {
            // User closed the popup without paying — not an error.
            ondismiss: () => resolve(),
          },
          theme: { color: "#4f46e5" },
        });
        rzp.on("payment.failed", (resp) => {
          reject(new Error(resp?.error?.description || "Payment failed."));
        });
        rzp.open();
      });
    } catch (e) {
      setError(e.message || "Payment failed. Please try again.");
    } finally {
      setPaying(false);
    }
  };

  if (success) {
    return (
      <div style={{ ...S.page, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", padding: 32 }}>
          <div style={{ fontSize: 80, marginBottom: 16 }}>✅</div>
          <h2 style={{ fontSize: 30, fontWeight: 900, color: "#0f172a", margin: "0 0 10px" }}>
            Payment Successful!
          </h2>
          <p style={{ color: "#64748b", fontSize: 15 }}>
            {tip > 0
              ? "Booking confirmed. Thanks for the tip! Redirecting home…"
              : "Booking confirmed. Redirecting to your orders…"}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={S.page}>
      <div style={S.container}>
        <button style={S.backBtn} onClick={() => navigate(-1)}>← Back</button>

        {/* Test mode notice */}
        {paymentOrder.is_test && (
          <div style={S.testBanner}>
            🧪 <strong>Test Mode</strong> — No real payment will be processed. Click "Pay Now" to simulate a successful payment.
          </div>
        )}

        <div style={S.header}>
          <p style={S.eyebrow}>Secure Payment</p>
          <h1 style={S.title}>Complete Your Payment</h1>
          <p style={S.subtitle}>Review your order details and confirm payment below.</p>
        </div>

        <div style={S.layout}>

          {/* ── Left: payment panel ── */}
          <div style={S.leftCol}>
            <div style={S.card}>
              <div style={S.cardTitle}>Payment Details</div>

              <div style={S.orderId}>
                Razorpay Order ID:{" "}
                <span style={S.orderIdVal}>{paymentOrder.order_id}</span>
              </div>

              {/* Bill breakdown */}
              <div style={S.breakdown}>
                <div style={S.bRow}>
                  <span>Base Fare</span>
                  <span>{fmt(subtotal)}</span>
                </div>
                {discount > 0 && (
                  <div style={S.bRow}>
                    <span style={{ color: "#16a34a" }}>Discount</span>
                    <span style={{ color: "#16a34a" }}>−{fmt(discount)}</span>
                  </div>
                )}
                {coupon?.amount > 0 && (
                  <div style={S.bRow}>
                    <span style={{ color: "#16a34a" }}>Coupon ({coupon.code})</span>
                    <span style={{ color: "#16a34a" }}>−{fmt(coupon.amount)}</span>
                  </div>
                )}
                <div style={S.bRow}>
                  <span>GST + Other Charges</span>
                  <span>{fmt(gst + platformFee)}</span>
                </div>
                {tip > 0 && (
                  <div style={S.bRow}>
                    <span>Partner Tip 🙌</span>
                    <span>{fmt(tip)}</span>
                  </div>
                )}
                <div style={{ ...S.bRow, ...S.bTotal }}>
                  <span>Total Payable</span>
                  <span>{fmt(total)}</span>
                </div>
              </div>

              {error && <div style={S.errorBox}>{error}</div>}

              <button
                style={{ ...S.payBtn, opacity: paying ? 0.65 : 1 }}
                disabled={paying}
                onClick={handlePay}
              >
                {paying ? "Processing…" : `Pay ${fmt(total)}`}
              </button>

              <div style={S.secureNote}>
                🔒 Secured by Razorpay
                {paymentOrder.is_test && <span style={S.testTag}> · TEST MODE</span>}
              </div>
            </div>
          </div>

          {/* ── Right: order summary ── */}
          <div style={S.rightCol}>
            <div style={S.card}>
              <div style={S.cardTitle}>Order Summary</div>

              <div style={S.serviceList}>
                {(orderPayload?.items || []).map((item, i) => (
                  <div key={i} style={S.svcRow}>
                    <div>
                      <div style={S.svcName}>{item.name || item.title || "Service"}</div>
                      {item.qty > 1 && (
                        <div style={S.svcQty}>Qty {item.qty}</div>
                      )}
                    </div>
                    <div style={S.svcPrice}>
                      {fmt(Number(item.price || 0) * Number(item.qty || 1))}
                    </div>
                  </div>
                ))}
              </div>

              {orderPayload?.delivery_address && (
                <div style={S.addrBox}>
                  <div style={S.addrLabel}>📍 Delivery Address</div>
                  <div style={S.addrText}>
                    {[
                      orderPayload.delivery_address.line1,
                      orderPayload.delivery_address.area,
                      orderPayload.delivery_address.city,
                    ].filter(Boolean).join(", ")}
                  </div>
                </div>
              )}

              {orderPayload?.instructions && (
                <div style={{ ...S.addrBox, marginTop: 10 }}>
                  <div style={S.addrLabel}>📝 Instructions</div>
                  <div style={S.addrText}>{orderPayload.instructions}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const S = {
  page:       { background: "#f1f5f9", minHeight: "100vh", padding: "32px 20px", fontFamily: "Inter,Arial,sans-serif" },
  container:  { maxWidth: 1020, margin: "0 auto" },
  backBtn:    { background: "none", border: "none", color: "#4f46e5", fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "0 0 20px", display: "block" },
  testBanner: { background: "#fefce8", border: "1px solid #fde68a", borderRadius: 12, padding: "12px 16px", fontSize: 14, color: "#92400e", marginBottom: 22 },
  header:     { marginBottom: 28 },
  eyebrow:    { margin: 0, color: "#4f46e5", fontSize: 13, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.08em" },
  title:      { margin: "8px 0 0", fontSize: 30, fontWeight: 900, color: "#0f172a" },
  subtitle:   { margin: "8px 0 0", fontSize: 15, color: "#64748b" },
  layout:     { display: "grid", gridTemplateColumns: "1fr 360px", gap: 24, alignItems: "start" },
  leftCol:    { display: "flex", flexDirection: "column", gap: 16 },
  rightCol:   { display: "flex", flexDirection: "column", gap: 16 },
  card:       { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 20, padding: "24px 22px" },
  cardTitle:  { fontSize: 18, fontWeight: 900, color: "#0f172a", marginBottom: 16 },
  orderId:    { fontSize: 12, color: "#64748b", marginBottom: 18, wordBreak: "break-all" },
  orderIdVal: { fontWeight: 700, color: "#4f46e5", fontFamily: "monospace" },
  breakdown:  { display: "flex", flexDirection: "column", gap: 10, marginBottom: 22 },
  bRow:       { display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 14, color: "#334155" },
  bTotal:     { borderTop: "2px solid #e5e7eb", paddingTop: 10, marginTop: 4, fontWeight: 900, fontSize: 18, color: "#0f172a" },
  errorBox:   { background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "10px 14px", color: "#b91c1c", fontSize: 13, marginBottom: 16 },
  payBtn:     { width: "100%", padding: "15px 0", borderRadius: 14, background: "linear-gradient(135deg,#4f46e5,#4338ca)", color: "#fff", fontSize: 17, fontWeight: 900, border: "none", cursor: "pointer", marginBottom: 12 },
  secureNote: { textAlign: "center", fontSize: 12, color: "#94a3b8", fontWeight: 600 },
  testTag:    { color: "#f59e0b", fontWeight: 800 },
  serviceList:{ display: "flex", flexDirection: "column", gap: 0, marginBottom: 10 },
  svcRow:     { display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "10px 0", borderBottom: "1px solid #f1f5f9" },
  svcName:    { fontSize: 14, fontWeight: 600, color: "#334155" },
  svcQty:     { fontSize: 12, color: "#94a3b8", marginTop: 2 },
  svcPrice:   { fontSize: 14, fontWeight: 800, color: "#1d4ed8", whiteSpace: "nowrap", marginLeft: 12 },
  addrBox:    { background: "#f8fafc", borderRadius: 12, padding: "10px 14px" },
  addrLabel:  { fontSize: 12, fontWeight: 700, color: "#64748b", marginBottom: 4 },
  addrText:   { fontSize: 13, color: "#334155", lineHeight: 1.6 },
};

export default PaymentPage;
